"use client";

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { tickerToSlug } from '@/lib/ticker-url';
import { triggerStockLoading } from '@/lib/stockNavigationFeedback';
import { useLoading } from '@/app/contexts/LoadingContext';
import BrailleSpinner from '@/app/components/BrailleSpinner';
import { useIndustryEmoji } from '@/lib/hooks/useListDisplayNames';
import { useBenchmarkEmojis } from '@/lib/hooks/useBenchmarkEmojis';
import CountryFlag from "@/lib/components/CountryFlag";
import SectorIcon from "@/lib/components/SectorIcon";
import EtfEmoji from "@/lib/components/EtfEmoji";
import BastionTooltip from "@/lib/components/BastionTooltip";
import { strategyRatingMomentumBars } from "@/app/heatmap/_lib/heatmap-grid-shared";
import { getLetterColor, hexToRgb } from '@/lib/stock/letterColor';

const FONT = 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif';
const META_ICON_SIZE = 11;
const STOCK_FIELDS = 'FullTicker, Ticker, Name, Country, industrygroup, Industry, PureMomentum';
const TRENDING_LIMIT = 10;
const EXPANDED_SEARCH_MAX_PX = 680;

const TREND_COLUMN_LABEL_STYLE: React.CSSProperties = {
  fontFamily: FONT,
  fontSize: 11,
  fontWeight: 500,
  color: 'rgba(255,255,255,0.35)',
  letterSpacing: '0.02em',
  flexShrink: 0,
  lineHeight: 1,
};

function waitForSearchExpand(input: HTMLInputElement, onReady: () => void): () => void {
  const container = input.closest('.topnav-search') as HTMLElement | null;
  if (!container || window.matchMedia('(max-width: 767px)').matches) {
    const id = requestAnimationFrame(onReady);
    return () => cancelAnimationFrame(id);
  }

  const targetWidth = Math.min(EXPANDED_SEARCH_MAX_PX, window.innerWidth * 0.46);
  let finished = false;

  const finish = () => {
    if (finished) return;
    finished = true;
    onReady();
  };

  let rafId = 0;
  const pollWidth = () => {
    if (container.getBoundingClientRect().width >= targetWidth - 2) {
      finish();
      return;
    }
    rafId = requestAnimationFrame(pollWidth);
  };

  const onTransitionEnd = (event: TransitionEvent) => {
    if (event.target === container && event.propertyName === 'max-width') {
      finish();
    }
  };

  container.addEventListener('transitionend', onTransitionEnd);
  rafId = requestAnimationFrame(pollWidth);
  const timeoutId = window.setTimeout(finish, 400);

  return () => {
    finished = true;
    cancelAnimationFrame(rafId);
    clearTimeout(timeoutId);
    container.removeEventListener('transitionend', onTransitionEnd);
  };
}

type StockOption = {
  FullTicker: string;
  Ticker: string;
  Name: string;
  Country: string;
  industrygroup: string;
  Industry: string;
  PureMomentum?: number | null;
};

type BrowseSection = {
  id: string;
  label: string;
  stocks: StockOption[];
};

function dedupeStockSections(sections: BrowseSection[]): BrowseSection[] {
  const seen = new Set<string>();
  return sections.map((section) => {
    const stocks: StockOption[] = [];
    for (const stock of section.stocks) {
      const key = String(stock.FullTicker || stock.Ticker || '').toUpperCase().trim();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      stocks.push(stock);
    }
    return { ...section, stocks };
  }).filter((section) => section.stocks.length > 0);
}

function SearchMomentumBars({ score }: { score: number | null | undefined }) {
  const heights = [7, 10, 14];

  if (score == null || !isFinite(score)) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          gap: 2,
          flexShrink: 0,
          width: 16,
          height: 15,
        }}
        aria-hidden="true"
      >
        {heights.map((h, i) => (
          <span
            key={i}
            style={{
              display: 'inline-block',
              width: 4,
              height: h,
              borderRadius: 1,
              background: 'rgba(255,255,255,0.12)',
            }}
          />
        ))}
      </span>
    );
  }

  const { filled, color: barColor } = strategyRatingMomentumBars(score);

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: 2,
        flexShrink: 0,
        width: 16,
        height: 15,
      }}
      aria-hidden="true"
    >
      {heights.map((h, i) => (
        <span
          key={i}
          style={{
            display: 'inline-block',
            width: 4,
            height: h,
            borderRadius: 1,
            background: i < filled ? barColor : 'rgba(255,255,255,0.12)',
          }}
        />
      ))}
    </span>
  );
}

function SearchSectionHeader({
  label,
  first = false,
  showTrendColumn = false,
}: {
  label: string;
  first?: boolean;
  showTrendColumn?: boolean;
}) {
  return (
    <div
      className="px-3 pb-1.5 flex items-center justify-between gap-3"
      style={{
        paddingTop: first ? 12 : 14,
        fontFamily: FONT,
        fontSize: 13,
        fontWeight: 600,
        color: '#ffffff',
        letterSpacing: '-0.01em',
        borderTop: first ? 'none' : '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <span>{label}</span>
      {showTrendColumn ? <span style={TREND_COLUMN_LABEL_STYLE}>Trend</span> : null}
    </div>
  );
}

function SearchTrendColumnHeader() {
  return (
    <div className="px-3 pt-3 pb-1 flex justify-end">
      <span style={TREND_COLUMN_LABEL_STYLE}>Trend</span>
    </div>
  );
}

function StockSearchLetterAvatar({ name, ticker }: { name: string; ticker: string }) {
  const letter = (name || ticker || '?')[0].toUpperCase();
  const color = getLetterColor(letter);
  const rgb = hexToRgb(color);

  return (
    <div
      aria-hidden="true"
      style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        background: `linear-gradient(135deg, rgba(${rgb}, 0.22) 0%, rgba(${rgb}, 0.10) 100%)`,
        border: `1px solid rgba(${rgb}, 0.40)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color,
        fontSize: 16,
        fontWeight: 700,
        fontFamily: FONT,
        letterSpacing: '-0.02em',
        lineHeight: 1,
      }}
    >
      {letter}
    </div>
  );
}

interface NavStockSearchProps {
  onNavigationStart?: () => void;
  onNavigationEnd?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  compact?: boolean;
  /** When provided, selecting a stock calls this instead of navigating to the stock page (e.g. add to watchlist). */
  onPickStock?: (stock: StockOption) => void | Promise<void>;
  /** Override input placeholder */
  placeholder?: string;
}

function StockSearchResultRow({
  stock,
  isLast,
  onSelect,
  onPickStock,
  emojiForIndustry,
  getBenchmarkEmoji,
}: {
  stock: StockOption;
  isLast: boolean;
  onSelect: (stock: StockOption) => void;
  onPickStock?: (stock: StockOption) => void | Promise<void>;
  emojiForIndustry: (raw?: string | null) => string;
  getBenchmarkEmoji: (fullTicker: string) => string;
}) {
  const isBenchmark = Boolean(stock.FullTicker?.endsWith('.B'));
  const benchmarkEmoji = isBenchmark ? getBenchmarkEmoji(stock.FullTicker) : null;
  const sectorEmoji = isBenchmark
    ? ''
    : stock.Industry
      ? (emojiForIndustry(stock.Industry) || emojiForIndustry(stock.industrygroup))
      : stock.industrygroup
        ? emojiForIndustry(stock.industrygroup)
        : '';
  const companyName = stock.Name || stock.FullTicker;
  const showMetaIcons = Boolean(benchmarkEmoji || (!isBenchmark && stock.Country) || sectorEmoji);

  return (
    <div
      onClick={() => onSelect(stock)}
      className="px-3 py-2.5 cursor-pointer transition-colors"
      style={{
        borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.06)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <StockSearchLetterAvatar name={companyName} ticker={stock.Ticker} />
        <div className="min-w-0 flex-1">
          <div
            className="truncate"
            style={{
              fontFamily: FONT,
              fontSize: 14,
              fontWeight: 600,
              color: 'rgba(255,255,255,0.95)',
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
            }}
          >
            {companyName}
          </div>
          <div className="flex items-center gap-1.5 mt-1 min-w-0">
            {showMetaIcons && (
              <span className="inline-flex items-center flex-shrink-0" style={{ gap: 3 }} aria-hidden="true">
                {benchmarkEmoji && <EtfEmoji emoji={benchmarkEmoji} size={META_ICON_SIZE} />}
                {!isBenchmark && stock.Country && (
                  <CountryFlag country={stock.Country} size={META_ICON_SIZE} />
                )}
                {sectorEmoji && <SectorIcon emoji={sectorEmoji} size={META_ICON_SIZE} />}
              </span>
            )}
            <span
              className="truncate"
              style={{
                fontFamily: FONT,
                fontSize: 12,
                fontWeight: 700,
                color: '#d4a54a',
                letterSpacing: '0.04em',
                lineHeight: 1.2,
              }}
            >
              {stock.Ticker}
            </span>
          </div>
        </div>
        <BastionTooltip
          text={
            stock.PureMomentum != null && isFinite(stock.PureMomentum)
              ? `Price Momentum: ${Math.round(stock.PureMomentum)} / 100`
              : 'Price Momentum: no data'
          }
          zIndex={10001}
        >
          <SearchMomentumBars score={stock.PureMomentum} />
        </BastionTooltip>
        {onPickStock && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(stock);
            }}
            className="flex-shrink-0 text-xs font-semibold px-3 py-1 rounded-full border border-[rgba(255,255,255,0.35)] text-white hover:bg-[rgba(255,255,255,0.08)] transition-colors"
          >
            Add
          </button>
        )}
      </div>
    </div>
  );
}

export default function NavStockSearch({ onNavigationStart, onNavigationEnd, onFocus, onBlur, compact, onPickStock, placeholder }: NavStockSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<StockOption[]>([]);
  const [browseSections, setBrowseSections] = useState<BrowseSection[]>([]);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const { setLoading: setGlobalLoading, setLoadingMessage: setGlobalLoadingMessage } = useLoading();
  const navigatingToRef = useRef<string | null>(null);

  const getIndustryEmoji = useIndustryEmoji();
  const getBenchmarkEmoji = useBenchmarkEmojis();
  
  const emojiForIndustry = useCallback((raw?: string | null) => {
    if (!raw) return "";
    return getIndustryEmoji(raw, 'industry') || getIndustryEmoji(raw, 'industrygroup');
  }, [getIndustryEmoji]);

  const dropdownStyle: React.CSSProperties = {
    overscrollBehavior: 'contain',
    width: '100%',
  };

  const dismissSearch = useCallback((blurInput = true) => {
    setShowDropdown(false);
    setIsFocused(false);
    if (blurInput) {
      inputRef.current?.blur();
    }
    onBlur?.();
  }, [onBlur]);

  useEffect(() => {
    if (!isFocused) {
      setShowDropdown(false);
      return;
    }

    const input = inputRef.current;
    if (!input) return;

    let cancelled = false;
    const cleanup = waitForSearchExpand(input, () => {
      if (!cancelled) setShowDropdown(true);
    });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [isFocused]);

  // Close when clicking outside, switching windows, or hiding the tab.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        dismissSearch(true);
      }
    };

    const handleWindowBlur = () => {
      dismissSearch(false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        dismissSearch(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dismissSearch]);

  // Default browse list: watchlist, then trending (top momentum from usa100).
  useEffect(() => {
    if (!isFocused || searchTerm.trim()) {
      return;
    }

    let cancelled = false;
    setBrowseLoading(true);

    const loadBrowseSections = async () => {
      const supabase = createClient();

      const [sessionRes, trendingRes] = await Promise.all([
        supabase.auth.getSession(),
        fetch('/api/stocks/search').then((r) => (r.ok ? r.json() : { stocks: [] })),
      ]);

      if (cancelled) return;

      let watchlistStocks: StockOption[] = [];

      const userId = sessionRes.data.session?.user?.id;
      if (userId) {
        const { data: wlData } = await supabase
          .from('watchlists')
          .select('watchlist_items(ticker)')
          .eq('user_id', userId);

        if (cancelled) return;

        const watchlistTickers = [
          ...new Set(
            (wlData ?? []).flatMap((wl: { watchlist_items?: { ticker?: string }[] }) =>
              (wl.watchlist_items ?? [])
                .map((item) => String(item.ticker || '').toUpperCase().trim())
                .filter(Boolean),
            ),
          ),
        ];

        if (watchlistTickers.length > 0) {
          const { data: wlStocks } = await supabase
            .from('stocks_clean')
            .select(STOCK_FIELDS)
            .in('FullTicker', watchlistTickers);

          if (cancelled) return;

          const byTicker = new Map(
            ((wlStocks ?? []) as StockOption[]).map((s) => [s.FullTicker.toUpperCase(), s]),
          );
          watchlistStocks = watchlistTickers
            .map((ticker) => byTicker.get(ticker))
            .filter((stock): stock is StockOption => Boolean(stock));
        }
      }

      const trendingStocks = (trendingRes?.stocks ?? []) as StockOption[];

      const sections = dedupeStockSections([
        { id: 'watchlist', label: 'Your Watchlist', stocks: watchlistStocks },
        { id: 'trending', label: 'Trending Stocks', stocks: trendingStocks },
      ]).map((section) =>
        section.id === 'trending'
          ? { ...section, stocks: section.stocks.slice(0, TRENDING_LIMIT) }
          : section,
      );

      setBrowseSections(sections);
      setBrowseLoading(false);
    };

    void loadBrowseSections().catch((error) => {
      console.error('Error loading browse stocks:', error);
      if (!cancelled) {
        setBrowseSections([]);
        setBrowseLoading(false);
      }
    });

    const reloadBrowseSections = () => {
      if (cancelled) return;
      setBrowseLoading(true);
      void loadBrowseSections().catch((error) => {
        console.error('Error reloading browse stocks:', error);
        if (!cancelled) {
          setBrowseSections([]);
          setBrowseLoading(false);
        }
      });
    };

    window.addEventListener('watchlist-changed', reloadBrowseSections);

    return () => {
      cancelled = true;
      window.removeEventListener('watchlist-changed', reloadBrowseSections);
    };
  }, [isFocused, searchTerm]);

  // Поиск акций — поддержка от 1 символа, приоритет точных тикеров
  const searchStocks = async (query: string) => {
    if (query.length < 1) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    
    try {
      const upperQuery = query.toUpperCase();

      const res = await fetch(`/api/stocks/search?q=${encodeURIComponent(query)}`);
      if (!res.ok) throw new Error(`Search HTTP ${res.status}`);
      const { stocks } = (await res.json()) as { stocks?: StockOption[] };

      const sorted = (stocks || []).sort((a, b) => {
        const aExact = a.Ticker.toUpperCase() === upperQuery ? 0 : 1;
        const bExact = b.Ticker.toUpperCase() === upperQuery ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;
        
        const aStarts = a.Ticker.toUpperCase().startsWith(upperQuery) ? 0 : 1;
        const bStarts = b.Ticker.toUpperCase().startsWith(upperQuery) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        
        return 0;
      }).slice(0, 10);
      
      setSuggestions(sorted);
    } catch (error) {
      console.error('Error searching stocks:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    searchStocks(value);
    setIsFocused(true);
  };

  const handleSelectStock = (stock: StockOption) => {
    if (onPickStock) {
      void Promise.resolve(onPickStock(stock)).then(() => {
        setSearchTerm('');
        setShowDropdown(false);
        setIsFocused(false);
        setSuggestions([]);
      });
      return;
    }

    const targetPath = `/stock/${tickerToSlug(stock.FullTicker)}`;

    if (pathname === targetPath) {
      setSearchTerm('');
      setShowDropdown(false);
      setIsFocused(false);
      return;
    }
    
    setSearchTerm('');
    setShowDropdown(false);
    setIsFocused(false);
    setNavigating(true);
    navigatingToRef.current = stock.FullTicker;
    onNavigationStart?.();
    triggerStockLoading(setGlobalLoading, setGlobalLoadingMessage);
    router.push(targetPath);
  };
  
  useEffect(() => {
    if (navigating && navigatingToRef.current) {
      const target = navigatingToRef.current;
      const expectedPath = `/stock/${tickerToSlug(target)}`;
      if (pathname === expectedPath) {
        setNavigating(false);
        navigatingToRef.current = null;
        onNavigationEnd?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
  
  useEffect(() => {
    if (navigating) {
      const timer = setTimeout(() => {
        setNavigating(false);
        navigatingToRef.current = null;
        onNavigationEnd?.();
      }, 15000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigating]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && suggestions.length > 0) {
      handleSelectStock(suggestions[0]);
    }
    if (e.key === 'Escape') {
      dismissSearch(true);
      setSearchTerm('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (suggestions.length > 0) {
      handleSelectStock(suggestions[0]);
    }
  };

  const handleDropdownWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const isAtTop = el.scrollTop <= 0;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const isActive = isFocused;

  return (
    <div className={`relative w-full${isActive ? ' nav-stock-search--active' : ''}`} ref={dropdownRef}>
      <form onSubmit={handleSubmit} className="relative w-full">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            onBlur?.();
          }}
          placeholder={placeholder ?? (compact ? "Search stocks" : "Search stocks...")}
          className={`w-full outline-none transition-all duration-200 ${
            compact
              ? 'pl-9 pr-7 h-[38px] rounded-[10px] text-[16px] sm:text-[14px]'
              : 'pl-9 pr-8 h-10 rounded-[10px] text-[16px] sm:text-[15px]'
          } ${
            isActive
              ? 'text-[rgba(255,255,255,0.92)] placeholder-[rgba(255,255,255,0.35)] bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.12)] focus:border-[rgba(255,255,255,0.18)] focus:bg-[rgba(255,255,255,0.05)]'
              : 'text-[#dbdbdb] placeholder-[#999] bg-[#1e1e1e] border border-[#444] focus:border-[#3bd576] focus:bg-[#1f1f1f]'
          }`}
          style={{ fontFamily: FONT }}
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60">
          <svg width={compact ? "16" : "18"} height={compact ? "16" : "18"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
        </span>
        {(loading || navigating) && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <BrailleSpinner label="" size={compact ? 18 : 22} />
          </div>
        )}
        {searchTerm && !loading && !navigating && (
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setSuggestions([]);
              setShowDropdown(false);
              setIsFocused(false);
            }}
            aria-label="Clear"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}
      </form>

      {showDropdown && !searchTerm.trim() && (
        <div
          className="absolute top-full left-0 mt-1.5 rounded-[12px] z-50 nav-stock-search-dropdown overflow-y-auto custom-dropdown-scrollbar"
          onWheel={handleDropdownWheel}
          style={dropdownStyle}
        >
          {browseLoading ? (
            <div className="px-4 py-6 flex justify-center">
              <BrailleSpinner label="" size={28} />
            </div>
          ) : browseSections.length > 0 ? (
            browseSections.map((section, sectionIdx) => (
              <div key={section.id}>
                <SearchSectionHeader
                  label={section.label}
                  first={sectionIdx === 0}
                  showTrendColumn={sectionIdx === 0}
                />
                {section.stocks.map((stock, idx) => {
                  const isLastInSection = idx === section.stocks.length - 1;
                  const isLastOverall =
                    sectionIdx === browseSections.length - 1 && isLastInSection;
                  return (
                    <StockSearchResultRow
                      key={`${section.id}-${stock.FullTicker}`}
                      stock={stock}
                      isLast={isLastOverall}
                      onSelect={handleSelectStock}
                      onPickStock={onPickStock}
                      emojiForIndustry={emojiForIndustry}
                      getBenchmarkEmoji={getBenchmarkEmoji}
                    />
                  );
                })}
              </div>
            ))
          ) : (
            <div className="px-4 py-3 text-[rgba(255,255,255,0.45)] text-sm text-center" style={{ fontFamily: FONT }}>
              No data
            </div>
          )}
        </div>
      )}

      {showDropdown && searchTerm.trim() && suggestions.length > 0 && (
        <div 
          className="absolute top-full left-0 mt-1.5 rounded-[12px] z-50 nav-stock-search-dropdown overflow-y-auto custom-dropdown-scrollbar"
          onWheel={handleDropdownWheel}
          style={dropdownStyle}
        >
          <SearchTrendColumnHeader />
          {suggestions.map((stock, idx) => (
            <StockSearchResultRow
              key={stock.FullTicker}
              stock={stock}
              isLast={idx === suggestions.length - 1}
              onSelect={handleSelectStock}
              onPickStock={onPickStock}
              emojiForIndustry={emojiForIndustry}
              getBenchmarkEmoji={getBenchmarkEmoji}
            />
          ))}
        </div>
      )}

      {showDropdown && searchTerm.trim().length >= 1 && suggestions.length === 0 && !loading && (
        <div 
          className="absolute top-full left-0 mt-1.5 rounded-[12px] z-50 nav-stock-search-dropdown" 
          style={dropdownStyle}
        >
          <div className="px-4 py-3 text-[rgba(255,255,255,0.45)] text-sm text-center" style={{ fontFamily: FONT }}>
            No stocks found for &ldquo;{searchTerm}&rdquo;
          </div>
        </div>
      )}
    </div>
  );
}
